// ─── La griglia campi × orari ───────────────────────────────────────────────
//
// 🎯 È la schermata su cui la segreteria sta tutto il giorno (§ 03.3).
//
// ⭐⭐ L'ASSE DEL TEMPO È IN MINUTI, NON IN CELLE, ed è la decisione che tiene
// in piedi tutto il resto. Le righe della griglia valgono un quarto d'ora
// ciascuna e ogni cosa — una fascia, una chiusura, una partita — si colloca
// dicendo da che minuto a che minuto va.
//
// ⛔ L'alternativa — una riga per «slot» — sembra più semplice e si rompe al
// primo dato vero: una lezione di sessanta minuti dentro una griglia da
// novanta, due campi con passi diversi, una prenotazione arrivata da fuori a
// un'ora che la griglia non offre. Tutte cose che ESISTONO, e che una griglia
// a celle o non sa disegnare o disegna spostate. 📌 *Una griglia che può
// mostrare solo ciò che saprebbe creare nasconde proprio i casi che vanno
// guardati.*

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  giorniFra,
  ETICHETTE_CADENZA,
  comeSiRipete,
  intervalliDelGiorno,
  intervalliFuoriFascia,
  minutiDaOra,
  oraDaMinuti,
  passiDelGiorno,
  ETICHETTE_CONFERMA,
  accorciaElenco,
  passiFuoriFascia,
  spazioLibero,
  vuoleMaestro,
  statoIncasso,
  mancaAQualcuno,
  puoAvereDenaro,
  campoSaldato,
  riservateDelGiorno,
  type Calendario,
  type Campo,
  type OraRiservata,
  type OrarioCircolo,
  type Prenotazione,
} from '@pmo/shared';
import { euro } from '../../routes/impostazioni/comune.js';

/** Un quarto d'ora per riga: il minimo comune multiplo dei casi veri. */
const RISOLUZIONE = 15;
/** Sotto questa distanza in pixel è un CLICK, non un trascinamento. */
const SOGLIA_TRASCINAMENTO = 5;
/** Quanto vicino al bordo bisogna arrivare perché la griglia scorra da sé. */
const MARGINE_SCORRIMENTO = 44;

/**
 * ⭐⭐ QUANTO È ALTA UNA RIGA — e dal 16/09/2026 NON È PIÙ UNA COSTANTE.
 *
 * 🗣️ Richiesta sua: *«ho bisogno di vedere tutta la giornata, non solo una
 * frazione e poi scrollare»*.
 *
 * 📏 Il difetto, misurato: la riga era **18 px fissi** (72 px l'ora) e il
 * riquadro era alto `min(76vh, 900px)`. Sopra ci stavano ~300 px di intestazioni
 * ⇒ 76vh + 300 px è **più alta della finestra**, e scorrevano tutte e due le
 * cose: la griglia dentro di sé **e** la pagina sotto di lei.
 *
 * ⇒ In vista `intera` l'altezza della riga è **quella che serve perché la
 *   giornata ci stia**: si misura lo spazio fra la cima della griglia e il fondo
 *   della finestra e lo si divide per le righe del giorno.
 * ⚠️ E le altre due viste restano, perché stringere ha un prezzo: sotto una
 *   certa altezza i nomi dei giocatori non ci stanno più (⇒ `base.css`, le
 *   interrogazioni di contenitore). Chi ha bisogno di leggere invece che di
 *   vedere tutto sceglie `comoda`.
 */
export type Vista = 'intera' | 'comoda' | 'grande';

export const VISTE: Array<{ valore: Vista; testo: string }> = [
  { valore: 'intera', testo: 'tutta la giornata' },
  { valore: 'comoda', testo: 'comoda' },
  { valore: 'grande', testo: 'grande' },
];

/** Le due viste a misura fissa. `comoda` è esattamente com'era prima. */
const ALTEZZE_FISSE: Record<Exclude<Vista, 'intera'>, number> = { comoda: 18, grande: 28 };

/**
 * 🚨 Il pavimento: sotto i 6 px per quarto d'ora un blocco da 90 minuti è alto
 * 36 px e non ci sta più niente dentro. Una giornata così lunga da non entrarci
 * nemmeno a questa misura **scorre**, ed è la risposta onesta: schiacciarla
 * ancora vorrebbe dire mostrarla senza farla leggere.
 */
const RIGA_MINIMA = 6;
/** Quanto si lascia sotto la griglia, perché la finestra non prenda a scorrere. */
const MARGINE_SOTTO = 20;
/** Sotto questa altezza la griglia non si stringe più: si accetta di scorrere. */
const SPAZIO_MINIMO = 320;

export interface GrigliaProps {
  calendario: Calendario;
  data: string;
  campi: Campo[];
  /** ⏰ Da che ora a che ora si disegna: l'orario del circolo, non le fasce. */
  orario: OrarioCircolo;
  vista: Vista;
  puoModificare: boolean;
  apriScheda(id: string): void;
  /**
   * ⭐⭐ NON PORTA PIÙ UNA DURATA, dal 16/09/2026 — e non è una semplificazione:
   * è la stessa distinzione su cui il committente mi aveva già corretto per le
   * fasce. **Il passo governa DOVE SI COMINCIA, non QUANTO SI STA**
   * (⇒ `griglia.ts`).
   *
   * 📏 Passando il passo come durata, col passo a 30 minuti ogni prenotazione
   * nuova nasceva da 30′ e andava corretta a mano — mentre al Padel Village si
   * gioca 90′. Il difetto c'era anche prima e non si vedeva, perché il passo
   * era 90 e i due numeri coincidevano per caso.
   * 📌 *Due grandezze diverse che per un po' hanno lo stesso valore non sono
   *    la stessa grandezza: sono un difetto che aspetta che una delle due
   *    cambi.*
   *
   * `fuoriFascia` ⇒ quell'ora i soci non la possono prenotare, la segreteria sì.
   *
   * ⭐ `spazio` sono i minuti liberi da lì al primo ostacolo (⇒ `spazioLibero`).
   * ⚠️ **Non è la durata che torna dalla porta di servizio**: la riga qui sopra
   * regge intera — il passo non governa quanto si sta. Questo è un **tetto**,
   * non una proposta: dice quanto ci si può stare al massimo, e serve perché
   * una scheda che nasce da 90′ dove ce ne stanno 30 viene rifiutata da «Crea»
   * dopo che la segreteria l'ha già compilata.
   */
  nuova(courtId: string, time: string, fuoriFascia: boolean, spazio: number, riservata?: OraRiservata): void;
  sposta(b: Prenotazione, courtId: string, time: string): void;
  cambiaDurata(b: Prenotazione, durationMinutes: number): void;
}

interface Trascinamento {
  prenotazione: Prenotazione;
  modo: 'sposta' | 'durata';
  xPartenza: number;
  yPartenza: number;
  /** Dov'è il dito ADESSO: è quello che la copia in mano insegue. */
  x: number;
  y: number;
  /**
   * Quanto era grande e dove stava il blocco quando lo si è preso.
   *
   * ⚖️ Si misura UNA volta, al `pointerdown`, e non a ogni movimento: durante il
   * trascinamento la griglia può scorrere da sé (⇒ `scorrimento`), quindi il
   * blocco di partenza si sposta sotto il dito. Rimisurarlo farebbe scappare la
   * copia in mano proprio mentre si cerca di tenerla ferma sul puntatore.
   */
  riquadro: { x: number; y: number; larghezza: number; altezza: number } | null;
  partito: boolean;
  /** Dove si finirebbe adesso: mostrato prima di lasciare il dito. */
  campo: string;
  ora: string;
  durata: number;
}

export function Griglia(props: GrigliaProps): React.JSX.Element {
  const { calendario, data, campi, orario, vista, puoModificare } = props;
  const [trascina, setTrascina] = useState<Trascinamento | null>(null);
  const [scorrimento, setScorrimento] = useState(0);
  const contenitore = useRef<HTMLDivElement>(null);

  /**
   * ⭐ La griglia SCORRE DA SÉ quando si trascina verso il bordo.
   *
   * 📏 Trovato trascinando davvero: la giornata è più alta dello schermo, e
   * senza questo una partita delle 09:30 non si può portare alle 21:00 — il
   * bersaglio non è raggiungibile, e il gesto sembra semplicemente non
   * funzionare. ⇒ Un timer, e non un salto a ogni movimento del dito: chi tiene
   * fermo il puntatore sul bordo deve vedere la griglia continuare a scorrere.
   */
  useEffect(() => {
    if (scorrimento === 0) return;
    const t = setInterval(() => {
      if (contenitore.current) contenitore.current.scrollTop += scorrimento * 14;
    }, 16);
    return () => clearInterval(t);
  }, [scorrimento]);

  /**
   * ⭐⭐ QUANTO SPAZIO C'È FRA LA CIMA DELLA GRIGLIA E IL FONDO DELLA FINESTRA.
   *
   * 🚨 `+ window.scrollY` non è un dettaglio: `getBoundingClientRect().top` è
   * la posizione nella FINESTRA, e scorrendo la pagina diventa più piccolo ⇒ la
   * griglia crescerebbe a ogni scorrimento, che a sua volta allunga la pagina.
   * Sommando lo scorrimento si ottiene la posizione nel DOCUMENTO, che non
   * dipende da dove si sta guardando.
   *
   * ⚖️ `useLayoutEffect` e non `useEffect`: la misura deve arrivare **prima**
   * che il browser dipinga, o il primo disegno esce con l'altezza sbagliata e si
   * vede un salto a ogni cambio di giorno.
   * ⚠️ L'osservatore guarda il `body` e non la finestra: la cima della griglia
   * si sposta anche senza che la finestra cambi — per esempio quando compare la
   * riga rossa del rifiuto di uno spostamento.
   *
   * 🆕⭐ **E GUARDA ANCHE LA SEZIONE CHE LA CONTIENE, dal 16/09/2026.** Col
   * pannello a fianco (⇒ `Pannello.tsx`) la griglia si RESTRINGE senza che la
   * finestra cambi di un pixel: la sezione prende 33rem di margine a destra.
   * ⇒ Restringendosi, la barra dei comandi può andare a capo — e allora la cima
   *   della griglia scende di una riga. Il `body`, che è alto quanto la finestra,
   *   può non accorgersene affatto: la griglia resterebbe alta come prima e
   *   uscirebbe di sotto, che è esattamente il difetto curato stamattina.
   * 📌 *Chi misura lo spazio deve guardare la cosa che glielo toglie, non quella
   *    che di solito cambia insieme.*
   */
  const testa = useRef<HTMLDivElement>(null);
  const [spazio, setSpazio] = useState(0);
  const [altaTesta, setAltaTesta] = useState(34);
  /**
   * 🚨 QUELLO CHE IL RIQUADRO SI MANGIA E NON È GRIGLIA: i due bordi da un
   * pixel, e la barra di scorrimento orizzontale quando c'è.
   *
   * 📏 Trovato misurando, non rileggendo: senza questi due pixel la giornata
   * era alta **esattamente quanto il riquadro**, e siccome `box-sizing` conta i
   * bordi dentro l'altezza, il contenuto sforava di 2 px ⇒ la barra di
   * scorrimento ricompariva. Due pixel, e la richiesta era «non voglio
   * scrollare».
   * ⚖️ `offsetHeight - clientHeight` li prende tutti e due insieme, e non
   * dipende dall'altezza che stiamo per calcolare: dipende solo dai bordi e
   * dalla larghezza del contenuto.
   */
  const [scarto, setScarto] = useState(2);

  useLayoutEffect(() => {
    function misura(): void {
      const el = contenitore.current;
      if (!el) return;
      const cima = el.getBoundingClientRect().top + window.scrollY;
      setSpazio(Math.max(SPAZIO_MINIMO, Math.round(window.innerHeight - cima - MARGINE_SOTTO)));
      setScarto(el.offsetHeight - el.clientHeight);
      if (testa.current) setAltaTesta(testa.current.offsetHeight);
    }
    misura();
    window.addEventListener('resize', misura);
    const osservatore = new ResizeObserver(misura);
    osservatore.observe(document.body);
    /* ⚠️ Nessun rischio di rincorsa: `misura` scrive gli stessi tre numeri se
       nulla è cambiato, e React non ridisegna per uno stato identico. */
    const sezione = contenitore.current?.parentElement;
    if (sezione) osservatore.observe(sezione);
    return () => {
      window.removeEventListener('resize', misura);
      osservatore.disconnect();
    };
  }, []);

  /**
   * ⭐⭐⭐ **CHI DECIDE SE I NOMI CI STANNO: IL BROWSER, NON UNA SOGLIA** —
   * 17/09/2026, e questa è una MARCIA INDIETRO su una scelta scritta ieri.
   *
   * 🗣️ Da cosa nasce, guardando una partita da 90 minuti coi cognomi puntati:
   *   *«non eravamo rimasti che se c'entravano mi facevi vedere nome e cognome
   *   per intero? Anche su due righe?»* — e aveva ragione: **c'entravano**.
   *
   * 🔄 Fino a ieri la scelta la faceva il CSS con delle soglie (sotto i 49 px
   * di blocco, sotto i 141 di larghezza, e da sei nomi in su). Il motivo
   * scritto era buono — *«quanto spazio c'è lo sa il CSS e deve restare l'unico
   * a saperlo»* — ma la premessa era falsa, e la misura lo dimostra.
   *
   * 📏 **LA MISURA CHE HA ROTTO LA REGOLA.** Larghezza minima perché QUATTRO
   * nomi stiano in due righe:
   *   · `Laura Aprea · Marco Aprea · Giulia Aprea · Lidia Comes` → **174 px**
   *   · `Massimiliano Bertoldi · Alessandra Santoro · …` → **276 px**
   * ⇒ Stesso numero di nomi, **102 px di differenza**. Una soglia tarata sui
   *   primi accorcia senza motivo; tarata sui secondi taglia via un giocatore
   *   intero coi puntini. Non esiste il numero giusto, perché **non dipende da
   *   quante persone ci sono: dipende da come si chiamano.**
   * 📌 *Una soglia è una misura fatta una volta sola su dati di esempio. Qui i
   *    dati sono i cognomi dei soci, e cambiano a ogni prenotazione.*
   *
   * 🔨 **Come si fa senza rimettere il difetto che le soglie evitavano**
   * (misurare a ogni fotogramma di un trascinamento): si scrive PRIMA e si
   * legge POI, una volta sola per tutti i blocchi insieme.
   *   ① si rimettono tutti INTERI (solo scritture);
   *   ② si guarda chi trabocca (solo letture: un calcolo di impaginazione per
   *      tutta la griglia, non uno per blocco);
   *   ③ si accorciano quelli che traboccano (solo scritture).
   * ⇒ Due impaginazioni per giro, non duecento. E il giro riparte solo quando
   *   cambia la geometria — mai durante un trascinamento, che sposta i blocchi
   *   senza cambiarne la misura.
   *
   * ⚠️ Non può rincorrersi: l'altezza di un blocco la decide la DURATA, non il
   * suo contenuto (⇒ `container-type: size`), quindi accorciare il testo non
   * cambia lo spazio disponibile e non può far ripartire la decisione.
   *
   * ⛔ Quando NEMMENO i nomi corti ci stanno resta il taglio coi puntini: è il
   * limite dello spazio, non una scelta. Ma prima si prova a mostrare tutto.
   */
  const firmaNomi = calendario.bookings
    .map((b) => `${b.id}:${b.players.map((g) => g.displayName).join(',')}:${b.instructorName ?? ''}`)
    .join('|');

  useLayoutEffect(() => {
    const el = contenitore.current;
    if (!el) return;

    function scegliLaForma(): void {
      const righe = el?.querySelectorAll<HTMLElement>('.giocatori');
      if (!righe) return;
      // ① tutti interi, e si scrive soltanto
      righe.forEach((r) => r.classList.remove('accorciati'));
      // ② si legge soltanto: `scrollHeight` è il testo intero, `clientHeight`
      //    quello che si vede — con `line-clamp` la differenza è ciò che il
      //    blocco taglierebbe via.
      const traboccano: HTMLElement[] = [];
      righe.forEach((r) => {
        if (r.scrollHeight > r.clientHeight + 1) traboccano.push(r);
      });
      // ③ e solo adesso si torna a scrivere
      traboccano.forEach((r) => r.classList.add('accorciati'));
    }

    scegliLaForma();
    /* ⚠️ L'osservatore guarda la griglia, non la finestra: col pannello aperto
       le colonne si dimezzano senza che la finestra cambi di un pixel — ed è
       proprio il caso in cui i nomi interi smettono di starci. */
    const osservatore = new ResizeObserver(scegliLaForma);
    osservatore.observe(el);
    return () => osservatore.disconnect();
  }, [firmaNomi, vista, spazio, altaTesta]);

  // ── L'asse del tempo ──────────────────────────────────────────
  //
  // ⭐ CI SONO SEMPRE L'APERTURA E LA CHIUSURA DEL CIRCOLO, e da qui nasce tutto
  // il resto: la giornata si disegna intera anche dove non c'è nessuna fascia.
  // Fino al 16/09/2026 gli estremi erano SOLO le fasce e le prenotazioni ⇒ un
  // mercoledì la cui prima fascia comincia alle 12:30 apriva il calendario a
  // mezzogiorno, e le ore in cui il circolo è aperto ma i soci non prenotano
  // **non esistevano affatto**.
  //
  // ⚠️ E comprende anche le FASCE e le PRENOTAZIONI, che possono stare fuori
  // dall'orario — una partita arrivata da un travaso, una fascia scritta prima
  // che l'orario esistesse, un orario stretto dopo. Una griglia che le taglia
  // fuori non le corregge: le nasconde.
  const inizioFine = campi.flatMap((c) => intervalliDelGiorno(calendario.slots, data, c.id));
  const prenotazioni = calendario.bookings.filter((b) => campi.some((c) => c.id === b.courtId));

  const estremi = [
    minutiDaOra(orario.opensAt),
    minutiDaOra(orario.closesAt),
    ...inizioFine.flat(),
    ...prenotazioni.flatMap((b) => {
      const da = giorniFra(data, b.date) * 1440 + minutiDaOra(b.time);
      return [da, da + b.durationMinutes];
    }),
  ];

  // ⚠️ Senza campi non c'è niente da disegnare, e non è un caso di fasce: è un
  //    circolo che non ha ancora detto quanti campi ha.
  if (campi.length === 0) {
    return (
      <div className="vuoto">
        <strong>Nessun campo</strong>
        <p className="tenue">
          Non c’è nessun campo in servizio da disegnare. I campi si creano e si riaccendono in{' '}
          <em>Impostazioni → Campi</em>.
        </p>
      </div>
    );
  }

  // Si arrotonda all'ora piena: una griglia che comincia alle 08:00 e non alle
  // 08:07 è una griglia che si legge.
  const daMin = Math.floor(Math.min(...estremi) / 60) * 60;
  const aMin = Math.ceil(Math.max(...estremi) / 60) * 60;
  const righe = Math.max(1, Math.ceil((aMin - daMin) / RISOLUZIONE));

  /**
   * ⭐ L'altezza della riga, che in vista `intera` è **calcolata** e non scelta.
   *
   * ⚠️ Finché la misura non c'è (`spazio === 0`, il primo disegno) si usa la
   * misura comoda invece di quella minima: se la misura fallisse del tutto, si
   * resterebbe con la griglia di sempre invece che con una striscia illeggibile.
   */
  const altezzaRiga =
    vista === 'intera'
      ? spazio === 0
        ? ALTEZZE_FISSE.comoda
        : Math.max(RIGA_MINIMA, (spazio - altaTesta - scarto) / righe)
      : ALTEZZE_FISSE[vista];

  /** Da minuti a linea della griglia CSS. La riga 1 è l'intestazione. */
  const linea = (m: number): number =>
    2 + Math.round((Math.min(Math.max(m, daMin), aMin) - daMin) / RISOLUZIONE);

  const ore: number[] = [];
  for (let m = daMin; m < aMin; m += 60) ore.push(m);

  // ── Il trascinamento ──────────────────────────────────────────────────────

  /**
   * 🚨⭐⭐ **IL BLOCCO CHE SI STA TRASCINANDO NON È UN BERSAGLIO PER SÉ STESSO**,
   * e questa riga è la cura di un difetto vero trovato il 16/09/2026 provando a
   * spostare una partita su una cella **libera**.
   *
   * 📏 Cosa succedeva, misurato guardando i bersagli degli eventi: mentre si
   * trascina, il blocco viene ridisegnato **sotto il dito**; `elementFromPoint`
   * trovava lui, e lui porta addosso `data-campo`/`data-ora` **di partenza** ⇒
   * la destinazione tornava sempre a essere quella di prima, e il gesto non
   * faceva niente. ⛔ Nessun errore, nessun messaggio: l'interfaccia sembrava
   * rotta senza esserlo.
   * ⚠️ E funzionava **per caso** quando il tragitto passava sopra un'altra
   * partita — che è esattamente il caso provato dalla Fase 4. Un difetto che si
   * nasconde dietro la prova che avrebbe dovuto trovarlo.
   *
   * ⇒ Si guarda **tutta la pila** sotto il dito (`elementsFromPoint`) e si salta
   *   il blocco trascinato. Le altre partite restano bersagli — lasciarne una
   *   sopra un'altra vuol dire «mettila lì», e a dire che lì non ci sta è il
   *   server, con le sue parole.
   */
  function celleSotto(x: number, y: number, escludi: string | null): { campo: string; ora: string } | null {
    for (const e of document.elementsFromPoint(x, y)) {
      const cella = (e as HTMLElement).closest<HTMLElement>('[data-campo][data-ora]');
      if (!cella) continue;
      if (escludi !== null && cella.dataset.id === escludi) continue;
      return { campo: cella.dataset.campo!, ora: cella.dataset.ora! };
    }
    return null;
  }

  /**
   * 🚨⭐⭐ **LA CATTURA DEL PUNTATORE VA AL CONTENITORE, NON AL BLOCCO**, e
   * questa riga è la seconda metà della cura del 16/09/2026.
   *
   * 📏 Misurato con gli eventi in mano: la cattura veniva presa dal blocco
   * (`gotpointercapture`) e **persa al primo movimento** (`lostpointercapture`),
   * perché quel blocco viene ridisegnato mentre lo si trascina e React ne
   * sostituisce il nodo — e una cattura muore col nodo che l'aveva presa.
   * ⇒ Da lì in poi gli eventi andavano alla cella sotto il dito, e i gestori
   *   attaccati al blocco non li vedevano più: né i movimenti, né il rilascio.
   *   **Il trascinamento su una cella libera non faceva niente.**
   *
   * ⚖️ Il contenitore invece non si ridisegna mai durante un trascinamento:
   * tiene la cattura fino alla fine, anche se il dito esce dalla griglia.
   * 📌 *Una cattura si dà a qualcosa che non cambia: chi si muove non può
   * tenere in mano il filo.*
   */
  function iniziaTrascinamento(e: React.PointerEvent, b: Prenotazione, modo: Trascinamento['modo']): void {
    if (!puoModificare) return;
    e.stopPropagation();
    contenitore.current?.setPointerCapture(e.pointerId);
    /*
     * ⚠️ `closest` e non `currentTarget`: prendendo la MANIGLIA della durata il
     * bersaglio è lei, larga sette pixel. Qui serve il blocco che la contiene.
     */
    const nodo = (e.currentTarget as HTMLElement).closest<HTMLElement>('.prenotazione');
    const r = nodo?.getBoundingClientRect();
    setTrascina({
      prenotazione: b,
      modo,
      xPartenza: e.clientX,
      yPartenza: e.clientY,
      x: e.clientX,
      y: e.clientY,
      riquadro: r ? { x: r.x, y: r.y, larghezza: r.width, altezza: r.height } : null,
      partito: false,
      campo: b.courtId,
      ora: b.time,
      durata: b.durationMinutes,
    });
  }

  function muovi(e: React.PointerEvent): void {
    if (!trascina) return;
    const dx = e.clientX - trascina.xPartenza;
    const dy = e.clientY - trascina.yPartenza;

    /**
     * 🚨 LA SOGLIA È UNA CURA GIÀ PAGATA, non una raffinatezza (§ 03.0): senza,
     * «il click del trascinamento si mangia la selezione» — si prova ad aprire
     * una scheda e si sposta la partita di un quarto d'ora.
     */
    const partito =
      trascina.partito || Math.abs(dx) > SOGLIA_TRASCINAMENTO || Math.abs(dy) > SOGLIA_TRASCINAMENTO;
    if (!partito) return;

    const cornice = contenitore.current?.getBoundingClientRect();
    if (cornice) {
      if (e.clientY < cornice.top + MARGINE_SCORRIMENTO) setScorrimento(-1);
      else if (e.clientY > cornice.bottom - MARGINE_SCORRIMENTO) setScorrimento(1);
      else setScorrimento(0);
    }

    if (trascina.modo === 'durata') {
      /*
       * 🚨⭐⭐ QUI C'ERA `ALTEZZA_RIGA`, LA COSTANTE A 18 PX, E DAL 16/09/2026
       * SAREBBE STATA UNA BUGIA.
       *
       * L'altezza della riga adesso dipende dalla finestra e dalla vista. Se
       * questa riga fosse rimasta ferma a 18, tirare il bordo di un blocco
       * avrebbe cambiato la durata di una quantità **sbagliata** — tre quarti
       * d'ora invece di uno, in vista `intera` — e non l'avrebbe detto nessun
       * errore: la partita si allunga, e te ne accorgi guardando.
       * 📌 *Una costante che descrive qualcosa di variabile non sbaglia subito:
       *    sbaglia il giorno in cui quel qualcosa comincia a variare.*
       */
      const passi = Math.round(dy / altezzaRiga);
      const durata = Math.max(RISOLUZIONE, trascina.prenotazione.durationMinutes + passi * RISOLUZIONE);
      setTrascina({ ...trascina, partito, durata, x: e.clientX, y: e.clientY });
      return;
    }

    const sotto = celleSotto(e.clientX, e.clientY, trascina.prenotazione.id);
    setTrascina({
      ...trascina,
      partito,
      x: e.clientX,
      y: e.clientY,
      campo: sotto?.campo ?? trascina.campo,
      ora: sotto?.ora ?? trascina.ora,
    });
  }

  function lascia(e: React.PointerEvent): void {
    if (!trascina) return;
    const t = trascina;
    setTrascina(null);
    setScorrimento(0);
    try {
      contenitore.current?.releasePointerCapture(e.pointerId);
    } catch {
      /* il puntatore può essere già stato rilasciato: non è un guasto */
    }

    // Sotto la soglia è un click: si apre la scheda, e non si sposta niente.
    if (!t.partito) {
      props.apriScheda(t.prenotazione.id);
      return;
    }
    if (t.modo === 'durata') {
      if (t.durata !== t.prenotazione.durationMinutes) props.cambiaDurata(t.prenotazione, t.durata);
      return;
    }
    if (t.campo !== t.prenotazione.courtId || t.ora !== t.prenotazione.time) {
      props.sposta(t.prenotazione, t.campo, t.ora);
    }
  }

  return (
    <div
      className="griglia-scorre"
      ref={contenitore}
      /*
       * ⚖️ L'altezza sta QUI e non nel CSS, perché il CSS non sa dove comincia
       * la griglia: `76vh` non conosce i 200 px di barra e intestazioni che ha
       * sopra, e infatti sommandoli usciva dalla finestra. In vista `intera` il
       * contenuto ci sta esatto e questo riquadro non scorre affatto; nelle
       * altre due scorre lui, e la PAGINA no — che era metà del difetto.
       */
      style={spazio > 0 ? { maxHeight: spazio } : undefined}
      // ⇒ E con la cattura, anche i gestori: un `pointerup` dato fuori dalla
      //   griglia deve finire il trascinamento, non lasciarlo a metà.
      onPointerMove={muovi}
      onPointerUp={lascia}
      onPointerCancel={lascia}
    >
      <div
        className="griglia"
        style={{
          gridTemplateColumns: `4.5rem repeat(${campi.length}, minmax(7rem, 1fr))`,
          gridTemplateRows: `auto repeat(${righe}, ${altezzaRiga}px)`,
        }}
      >
        <div className="angolo" ref={testa} style={{ gridColumn: 1, gridRow: 1 }} />
        {campi.map((c, i) => (
          <div key={c.id} className="testa-campo" style={{ gridColumn: i + 2, gridRow: 1 }}>
            {c.name}
          </div>
        ))}

        {ore.map((m) => (
          <div
            key={m}
            className="ora"
            style={{ gridColumn: 1, gridRow: `${linea(m)} / span ${60 / RISOLUZIONE}` }}
          >
            {oraDaMinuti(m % 1440)}
          </div>
        ))}

        {campi.map((campo, i) => {
          const colonna = i + 2;
          /**
           * ⭐⭐ DUE ELENCHI DI CELLE, E LA DIFFERENZA È **CHI** PUÒ PRENOTARLE.
           *
           * 🗣️ Sua richiesta del 16/09/2026: *«nel calendario devo vedere le fasce
           * non prenotabili, perché queste non sono prenotabili per i soci, ma noi
           * di segreteria possiamo prenotarle»*.
           *
           * ⚠️ Le celle fuori fascia NON sono decorazione: portano `data-campo` e
           * `data-ora` come tutte le altre, e devono. Senza, `celleSotto` non
           * troverebbe niente sotto il dito e trascinare una partita lì dentro
           * **non farebbe niente** — senza errore, senza messaggio, senza ritorno
           * indietro. È lo stesso difetto già pagato oggi con le prenotazioni che
           * si coprivano a vicenda, e si ripresenterebbe in un posto nuovo.
           */
          /*
           * 🚨⭐⭐ **E LE CELLE OCCUPATE NON SI OFFRONO — difetto suo, 18/09/2026:**
           * *«il calendario mostra verde uno slot già occupato»*, e lo si
           * scopriva solo premendo «Crea».
           *
           * 📏 Il perché, misurato: i due elenchi qui sopra nascono dalle sole
           * **fasce**. Una partita disegnata SOPRA una cella la copre solo per
           * quanto dura — quindi una 15:00–16:00 sopra la cella delle 15:30 ne
           * lasciava scoperto un pezzo, verde e cliccabile, **con l'orario
           * d'inizio già occupato**.
           * 📌 *Un bottone che si vede a metà non è un bottone a metà: è un
           *    bottone intero che mente sulla metà che si vede.*
           *
           * ⚖️ E non si filtra con `slotLiberi`, che pure esiste: quella chiede
           * una **durata**, e qui la durata non c'è ancora — la sceglie la
           * segreteria nella scheda. La domanda giusta è più corta: *da qui,
           * quanto spazio c'è?*
           */
          const celle = [
            ...passiDelGiorno(calendario.slots, data, campo.id).map((p) => ({ ...p, fuori: false })),
            ...passiFuoriFascia(calendario.slots, orario, data, campo.id).map((p) => ({
              ...p,
              fuori: true,
            })),
          ]
            .map((p) => ({
              ...p,
              spazio: spazioLibero(calendario.closures, prenotazioni, data, campo.id, minutiDaOra(p.time)),
            }))
            .filter((p) => p.spazio > 0);
          /* 🎓 18/10/2026 (mockup «Ore riservate»): le ore riservate a un maestro
             su questo campo. ⚠️ `?? []` per una risposta di prima, ancora in cassetto. */
          const riservate = riservateDelGiorno(calendario.reserved ?? [], data, campo.id);
          const riservataDi = (time: string): OraRiservata | undefined => {
            const t = minutiDaOra(time);
            return riservate.find((r) => minutiDaOra(r.startsAtTime) <= t && t < minutiDaOra(r.endsAtTime));
          };
          return (
            <div key={campo.id} className="colonna" style={{ display: 'contents' }}>
              {/*
                La banda grigia delle ore senza fascia. È disegnata PRIMA delle
                celle e non prende il puntatore: deve stare sotto, o coprirebbe
                proprio i bottoni che rende visibili.
                ⚖️ E non è rigata come una chiusura, apposta: le due cose si devono
                distinguere a colpo d'occhio, perché su una si può ancora cliccare
                e sull'altra no.
              */}
              {intervalliFuoriFascia(calendario.slots, orario, data, campo.id).map(([da, a]) => (
                <div
                  key={`fuori-${da}`}
                  className="fuori-fascia"
                  style={{ gridColumn: colonna, gridRow: `${linea(da)} / ${linea(a)}` }}
                >
                  <span>non prenotabile dai soci</span>
                </div>
              ))}

              {/*
                🎓 Le ore riservate: un riquadro a righe viola sul campo giusto,
                «🎓 Riservato · Marco R.» e l'orario. Sta SOTTO le celle e le
                prenotazioni, come la banda grigia: la parte libera resta da
                toccare, e apre la lezione con quel maestro.
              */}
              {riservate.map((r) => (
                <div
                  key={`riservata-${r.id}`}
                  className="riservata"
                  style={{
                    gridColumn: colonna,
                    gridRow: `${linea(minutiDaOra(r.startsAtTime))} / ${linea(minutiDaOra(r.endsAtTime))}`,
                  }}
                >
                  <span>
                    🎓 Riservato · {r.instructorName}
                    <small>
                      {r.startsAtTime}–{r.endsAtTime}
                    </small>
                  </span>
                </div>
              ))}

              {/* le celle su cui si può cominciare */}
              {celle.map((p) => {
                const da = minutiDaOra(p.time);
                const riservata = riservataDi(p.time);
                return (
                  <button
                    key={`${p.time}-${String(p.fuori)}`}
                    type="button"
                    className={`cella${p.fuori ? ' fuori' : ''}${riservata ? ' nel-riservato' : ''}`}
                    data-campo={campo.id}
                    data-ora={p.time}
                    disabled={!puoModificare}
                    /*
                     * 🚨 **ALTA QUANTO LA STANZA, non quanto il passo** — e non
                     * è estetica: misurato il 18/09/2026, una cella da 90′ con
                     * una partita alle 16:00 arrivava fin sotto il blocco, e il
                     * suo CENTRO cadeva dentro la partita. ⇒ Il bersaglio del
                     * click non stava sulla parte verde che si vede, e sotto il
                     * blocco restava un pezzo di bottone che nessuno poteva più
                     * raggiungere ma che c'era.
                     * 📌 *Un bersaglio che finisce sotto un altro non è
                     *    nascosto: è un bottone che ha smesso di coincidere con
                     *    la sua figura.*
                     */
                    style={{
                      gridColumn: colonna,
                      gridRow: `${linea(da)} / ${linea(da + Math.min(p.slotMinutes, p.spazio))}`,
                    }}
                    onClick={() => props.nuova(campo.id, p.time, p.fuori, p.spazio, riservata)}
                    title={
                      riservata
                        ? `${campo.name} — ${p.time} · riservato a ${riservata.instructorName}: lezione con ${riservata.instructorName}`
                        : p.fuori
                          ? `${campo.name} — ${p.time} · i soci qui non prenotano, la segreteria sì`
                          : `${campo.name} — ${p.time}`
                    }
                  >
                    <span className="ora-cella">{p.time}</span>
                  </button>
                );
              })}

              {/* le chiusure: si vedono come tali e non accettano il click */}
              {calendario.closures
                .filter((c) => c.date === data && (c.courtId === null || c.courtId === campo.id))
                .map((c) => (
                  <div
                    key={`${c.id}-${campo.id}`}
                    className="chiusa"
                    style={{ gridColumn: colonna, gridRow: `${linea(c.fromMinute)} / ${linea(c.toMinute)}` }}
                    title={c.reason ?? 'Chiuso'}
                  >
                    <span>{c.reason ?? 'Chiuso'}</span>
                  </div>
                ))}

              {/* le prenotazioni */}
              {prenotazioni
                .filter((b) => b.courtId === campo.id)
                .map((b) => {
                  const inMovimento = trascina?.partito === true && trascina.prenotazione.id === b.id;
                  /*
                   * 🚨⭐⭐ **QUI IL BLOCCO SPARIVA, ED È IL DIFETTO DEL 17/09/2026.**
                   *
                   * 🗣️ Parole sue: *«quando trascino una prenotazione sparisce
                   *   mentre la trascino, invece sarebbe carino potersela portare
                   *   appresso in modo visibile»*.
                   * 📏 Misurato prima di toccare niente: portando il dito su un
                   *   ALTRO campo i blocchi visibili passavano da **1 a 0**.
                   *   Dentro lo stesso campo il blocco c'era e saltava di slot in
                   *   slot; cambiando colonna spariva del tutto.
                   * ⚖️ Il perché stava in due righe che si contraddicevano: questo
                   *   ciclo prende le partite del campo **vero** (`b.courtId ===
                   *   campo.id`), e poi c'era un `if (campoDisegno !== campo.id)
                   *   return null` che le nascondeva appena il campo di ARRIVO era
                   *   un altro. ⇒ Nella colonna di partenza si toglieva, in quella
                   *   d'arrivo non entrava mai nell'elenco: nessuna delle due la
                   *   disegnava.
                   *
                   * ⇒ Adesso il blocco **non si muove affatto**: resta dov'è,
                   *   sbiadito, a dire *da qui parte*. A muoversi sono due cose
                   *   nuove — la copia sotto il dito (`.in-mano`, in fondo al
                   *   file) e il riquadro d'atterraggio (`.atterraggio`, qui
                   *   sotto).
                   * 📌 *Un blocco che si teletrasporta sul bersaglio non mostra il
                   *    gesto: mostra il risultato, e nel tragitto non c'è niente
                   *    da guardare.*
                   */
                  const oraDisegno = b.time;
                  const durataDisegno =
                    inMovimento && trascina.modo === 'durata' ? trascina.durata : b.durationMinutes;
                  const da = giorniFra(data, b.date) * 1440 + minutiDaOra(oraDisegno);
                  return (
                    <article
                      key={b.id}
                      className={`prenotazione tipo-${b.type}${saldata(b) ? ' saldata' : ''}${inMovimento ? (trascina.modo === 'sposta' ? ' in-partenza' : ' in-movimento') : ''}`}
                      /*
                       * 🚨 ANCHE UNA PRENOTAZIONE È UN BERSAGLIO, e senza queste
                       * due righe il criterio della Fase 4 non è raggiungibile.
                       *
                       * 📏 Trovato trascinando davvero, non rileggendo: un blocco
                       * copre la cella che sta sotto di lui, quindi lasciandogli
                       * sopra un'altra prenotazione `elementFromPoint` trovava
                       * LUI e non una cella — e lo spostamento non partiva
                       * affatto. ⇒ Nessun errore, nessun messaggio, nessun
                       * ritorno indietro: il gesto semplicemente non faceva
                       * niente, che è il modo in cui un'interfaccia sembra rotta
                       * senza esserlo.
                       * ⇒ Lasciare una partita sopra un'altra vuol dire «mettila
                       *   lì», e a dire che lì non ci sta è il server, con le sue
                       *   parole.
                       */
                      data-id={b.id}
                      data-campo={b.courtId}
                      data-ora={b.time}
                      style={{ gridColumn: colonna, gridRow: `${linea(da)} / ${linea(da + durataDisegno)}` }}
                      onPointerDown={(e) => iniziaTrascinamento(e, b, 'sposta')}
                      // Il click non apre la scheda: lo fa `lascia()`, che sa
                      // distinguere un click da un trascinamento finito sul posto.
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') props.apriScheda(b.id);
                      }}
                      title={`${oraDisegno} · ${durataDisegno}′${diCassa(b)}`}
                    >
                      <header>
                        <CheCosE b={b} />
                        <span className="tenue piccolo">{oraDisegno}</span>
                      </header>
                      <RigaNomi b={b} />
                      {b.totalDueCents > 0 && <span className="a-carico">{euro(b.totalDueCents)}</span>}
                      {puoModificare && (
                        <span
                          className="maniglia"
                          /*
                           * 🚨 SI RIMPICCIOLISCE COL BLOCCO, e non è estetica.
                           * A 7 px fissi, su uno slot da mezz'ora in vista
                           * `intera` (16 px alto) la maniglia sarebbe metà del
                           * blocco: si proverebbe a SPOSTARE la partita e le si
                           * cambierebbe la DURATA. Il quarto dell'altezza, con
                           * un pavimento di 4 px perché resti afferrabile.
                           */
                          style={{
                            height: Math.max(
                              4,
                              Math.min(7, ((durataDisegno / RISOLUZIONE) * altezzaRiga) / 4),
                            ),
                          }}
                          onPointerDown={(e) => iniziaTrascinamento(e, b, 'durata')}
                          title="Trascina per cambiare la durata"
                        />
                      )}
                    </article>
                  );
                })}

              {/*
                ⭐⭐ **DOVE ATTERRA**, disegnato nella colonna d'arrivo.
                ⚖️ È l'altra metà della copia in mano: quella dice *cosa* si sta
                muovendo e sta sotto il dito, questo dice *dove va a finire* e
                sta agganciato alla griglia — con l'ora scritta, perché fra due
                slot vicini il posto giusto non si distingue a occhio.
                ⛔ Non porta `data-campo`/`data-ora` e non prende il puntatore:
                   sarebbe un bersaglio sopra i bersagli, e `celleSotto`
                   troverebbe lui invece della cella.
              */}
              {trascina?.partito === true &&
                trascina.modo === 'sposta' &&
                trascina.campo === campo.id &&
                /* ⚖️ Non si disegna quando l'arrivo COINCIDE con la partenza:
                   là il posto è già segnato dal blocco spento, e i due cartellini
                   dell'ora finirebbero uno sull'altro. Lasciarlo vuoto dice la
                   cosa giusta — *tornerebbe dov'era*. */
                !(
                  trascina.campo === trascina.prenotazione.courtId &&
                  trascina.ora === trascina.prenotazione.time
                ) &&
                (() => {
                  const daAtt = minutiDaOra(trascina.ora);
                  return (
                    <div
                      className="atterraggio"
                      aria-hidden="true"
                      style={{
                        gridColumn: colonna,
                        gridRow: `${linea(daAtt)} / ${linea(daAtt + trascina.prenotazione.durationMinutes)}`,
                      }}
                    >
                      <span>{trascina.ora}</span>
                    </div>
                  );
                })()}
            </div>
          );
        })}
      </div>

      {/*
        ⭐⭐ **LA COPIA CHE SI PORTA APPRESSO.**
        🗣️ *«sarebbe carino potersela portare appresso in modo visibile»* — sua,
        17/09/2026.

        Sta FUORI dalla griglia e non dentro un blocco, e i due motivi sono
        misurati, non di gusto:
        · `position: fixed` vive in coordinate di finestra, quindi resta esatta
          sotto il dito **anche mentre la griglia scorre da sé** vicino al bordo;
        · `.prenotazione` ha `container-type: size`, che vuol dire `contain` —
          e un elemento con `contain` diventa il riquadro di riferimento dei
          suoi discendenti fissi. Una copia disegnata lì dentro resterebbe
          incollata al blocco di partenza invece di seguire il puntatore.

        ⛔ `pointer-events: none` è OBBLIGATORIO, e non per eleganza:
           `celleSotto` guarda cosa c'è sotto il dito, e una copia raggiungibile
           sarebbe sempre lei. ⚠️ Questa volta è sicuro — e il 16/09 non lo era:
           allora a portare `pointer-events: none` era il blocco **che teneva la
           cattura del puntatore**, e Chrome gliela toglieva. Qui la cattura sta
           sul contenitore, e questa copia non la tiene.
      */}
      {/*
        ⏱️⭐⭐ **QUANTO STA DIVENTANDO, MENTRE LO SI ALLUNGA** — 18/09/2026,
        richiesta sua: *«quando dal calendario trascino e amplio una
        prenotazione, man mano che lo faccio, mi fa vedere la durata che
        imposto»*.

        📏 **Cosa c'era prima, misurato sullo schermo**: il blocco cresceva e
          basta. L'unica scritta era il `title` del manico — *«Trascina per
          cambiare la durata»* — che dice **cosa stai facendo**, non **a che
          punto sei**; e l'ora in testa resta quella di **partenza**, che
          durante questo gesto è l'unica che non cambia.
        ⇒ Quindi per sapere dove si stava arrivando bisognava lasciare il dito e
          guardare il risultato: cioè provare, e correggere.

        ⚖️ **Perché segue il puntatore e non sta DENTRO il blocco**, che era la
          strada più ovvia: un blocco da 30′ è alto ~23 px e ha `overflow:
          hidden` ⇒ l'etichetta ci verrebbe tagliata **proprio nel caso in cui
          serve di più**, cioè quando si sta allungando una cosa piccola. Qui
          invece si legge sempre, ed è dove stanno gli occhi — sul bordo che si
          sta tirando.
        📌 *Un'informazione che compare solo quando c'è spazio non è
           un'informazione: è una decorazione.*

        ⭐ E dice **tutte e due le cose**: la durata (quello che ha chiesto) e
          **l'ora di fine** (quello che si va a leggere davvero, perché una
          persona ragiona per «fino alle»).
      */}
      {trascina?.partito === true && trascina.modo === 'durata' && (
        <span
          className="durata-in-corso"
          aria-hidden="true"
          style={{ position: 'fixed', left: trascina.x + 16, top: trascina.y + 16 }}
        >
          {durataScritta(trascina.durata)} · fino alle{' '}
          {oraDaMinuti(minutiDaOra(trascina.prenotazione.time) + trascina.durata)}
        </span>
      )}

      {trascina?.partito === true && trascina.modo === 'sposta' && trascina.riquadro && (
        <article
          className={`prenotazione tipo-${trascina.prenotazione.type}${saldata(trascina.prenotazione) ? ' saldata' : ''} in-mano`}
          aria-hidden="true"
          style={{
            position: 'fixed',
            left: trascina.riquadro.x + (trascina.x - trascina.xPartenza),
            top: trascina.riquadro.y + (trascina.y - trascina.yPartenza),
            width: trascina.riquadro.larghezza,
            height: trascina.riquadro.altezza,
          }}
        >
          <header>
            <CheCosE b={trascina.prenotazione} />
            {/* ⭐ L'ora scritta è quella di ARRIVO, non quella di partenza: è la
                cosa che si sta decidendo, ed è dove stanno gli occhi. */}
            <span className="tenue piccolo">{trascina.ora}</span>
          </header>
          <RigaNomi b={trascina.prenotazione} />
          {trascina.prenotazione.totalDueCents > 0 && (
            <span className="a-carico">{euro(trascina.prenotazione.totalDueCents)}</span>
          )}
        </article>
      )}
    </div>
  );
}

/**
 * ⭐⭐ **CHE COS'È, IN TESTA AL BLOCCO — e su una lezione c'è anche CHI LA
 * TIENE.**
 *
 * 🗣️ Sua domanda del 17/09/2026, guardando un blocco «Lezione» con il maestro in
 *   corsivo in mezzo agli iscritti: *«è fattibile che il nome del maestro in una
 *   lezione oppure in un corso stia a fianco alla tipologia?»*.
 * ⚖️ **Sì, ed è il posto giusto**: «Lezione» da sola non dice quale lezione, e
 *   il maestro non è uno dei presenti — è ciò che **identifica la cosa**, come
 *   il campo e l'ora. In coda al titolo si legge in un colpo; in mezzo ai nomi
 *   era il primo di un elenco, cioè sembrava un iscritto scritto in corsivo.
 * ⭐ E si accorcia per primo: il titolo sta su una riga sola e non ruba spazio
 *   ai nomi, che in una colonna stretta sono l'unica cosa che il calendario dà
 *   e la scheda no. Nel blocco c'è il nome CORTO — il cognome per esteso lo si
 *   legge aprendo la scheda.
 * 🔄 **Solo dove c'entra** — sua, 17/09 sera: *«quando seleziono manutenzione,
 *   se per sbaglio è inserito un maestro non deve apparire sul calendario; lo
 *   stesso vale per la partita e per il torneo»*. ⇒ Si legge `VUOLE_MAESTRO`,
 *   non `instructorName !== null`: una manutenzione con dentro un maestro
 *   rimasto da un cambio di tipo **non lo mostra**, e non serve andare a
 *   ripulire le righe già in colonna.
 */
/**
 * Il campo è saldato ⇒ il blocchetto è tutto verde. La REGOLA sta in `shared`
 * (`campoSaldato`), qui c'è solo la scorciatoia che le passa i tre pezzi.
 */
function saldata(b: Prenotazione): boolean {
  return campoSaldato(b.type, b.players, b.incassatoCents);
}

/**
 * ⭐⭐ LE CIFRE, A PAROLE, passando sopra il blocchetto.
 *
 * 🚨 **Dal 18/09/2026 è l'UNICO canale che non è un colore**, e questo la rende
 * più importante di prima, non meno: la barretta è stata tolta (*«leva la
 * barra, non serve più, tanto non la vede nessuno»*), quindi il fondo verde
 * risponde da solo — e a chi il verde non lo distingue, o legge con la
 * tastiera e col lettore di schermo, non arriverebbe altrimenti niente.
 *
 * ⚠️ E dice una cosa che il colore NON dice più: **quanto** manca. Col verde
 * acceso o spento, una partita pagata a metà e una non pagata hanno lo stesso
 * aspetto; qui si distinguono.
 */
function diCassa(b: Prenotazione): string {
  if (!puoAvereDenaro(b.type)) return '';
  const stato = statoIncasso(b.players, b.incassatoCents);
  if (stato === 'niente') return ' · importo non ancora deciso';
  // 🗣️ «Si scrive incassato, non incassata» — sua, 18/09/2026.
  if (stato === 'incassata') return ` · incassato (${euro(b.incassatoCents)})`;
  /*
   * 🚨 **QUANTO MANCA lo dice `mancaAQualcuno`, non una sottrazione fra i due
   * totali** — e non è pignoleria: con 15,00 € a carico e 15,50 € in cassa
   * (perché chi è uscito ha lasciato lì i suoi soldi) quella sottrazione dà
   * −0,50, e il blocchetto avrebbe scritto «mancano −0,50 €» mentre una
   * persona non aveva versato un euro.
   */
  const manca = mancaAQualcuno(b.players);
  return stato === 'da-incassare'
    ? ` · da incassare: ${euro(manca)}`
    : ` · incassato ${euro(b.incassatoCents)}, manca ${euro(manca)}`;
}

function CheCosE({ b }: { b: Prenotazione }): React.JSX.Element {
  const maestro = vuoleMaestro(b.type) ? b.instructorName : null;
  return (
    <span className="che-cos-e">
      {/* ⚠️ Il `title`: su una colonna stretta il nome si accorcia coi puntini
          (⇒ `base.css`), e «Manuten…» va potuto rileggere per esteso senza
          aprire la scheda — come già fa il maestro qui sotto. */}
      <strong title={nomeDellaPrenotazione(b)}>{nomeDellaPrenotazione(b)}</strong>
      {maestro !== null && (
        <em className="maestro" title={maestro}>
          {b.instructorShortName ?? maestro}
        </em>
      )}
      {/*
        🔁 **Un segno solo per dire «si ripete»**, e non una parola: nel
        blocchetto ogni carattere è spazio tolto ai nomi, e chi ha bisogno di
        sapere quanto dura la serie apre la scheda — dove sta scritto per
        esteso. Qui serve a rispondere a una domanda sola: *questa è una delle
        tante?*
      */}
      {b.serie !== null && (
        <span
          className="si-ripete"
          title={`Si ripete ${ETICHETTE_CADENZA[b.serie.cadenza]}, ${comeSiRipete(b.serie.dal, b.serie)}, fino al ${b.serie.fino}${b.serie.dalBot ? ' · chiesto dal socio, dal bot' : ''}${b.serie.rinnovo === 'non_rinnova' ? ' · non rinnova' : b.serie.rinnovo === 'rinnovato' ? ' · rinnovato' : ''}`}
          aria-label={b.serie.dalBot ? 'si ripete, chiesto dal bot' : 'si ripete'}
        >
          {b.serie.dalBot ? '↻ bot' : '↻'}
        </span>
      )}
      {/* La conferma chiesta all'organizzatore: un segno, e le parole passandoci sopra. */}
      <SegnoConferma b={b} />
    </span>
  );
}

/**
 * 🆕 Fase 2 (26/09/2026) — **la conferma chiesta all'organizzatore dal bot**: un
 * segno, e le parole passandoci sopra. Nessuna risposta non annulla niente: si
 * vede, e basta (§ 4.4). 🔄 30/09/2026: accanto c'era il segno dell'acconto, andato via.
 */
const SEGNI_CONFERMA = { confermata: '✅', chiesta: '❔', problema: '⚠️', ci_pensiamo: '🤝' } as const;

function SegnoConferma({ b }: { b: Prenotazione }): React.JSX.Element | null {
  if (!b.conferma || b.status === 'cancelled') return null;
  return (
    <span className="segno-caparra" title={ETICHETTE_CONFERMA[b.conferma]}>
      {SEGNI_CONFERMA[b.conferma]}
    </span>
  );
}

/**
 * ⭐⭐ **LA RIGA DEI NOMI DENTRO IL BLOCCHETTO**, e sta in un posto solo perché
 * la disegnano in DUE: il blocco sul calendario e la copia che si porta appresso
 * il trascinamento. Erano due copie a mano, e la seconda è esattamente il posto
 * dove una regola nuova si dimentica.
 *
 * 🔄 **Il maestro non sta più qui, dal 17/09/2026 sera** (⇒ `CheCosE`): questa
 * riga è di chi è in campo, e il maestro non è uno di loro.
 */
function RigaNomi({ b }: { b: Prenotazione }): React.JSX.Element | null {
  if (b.players.length === 0) return null;

  const voci = b.players.map((g) => ({ intero: g.displayName, corto: g.shortName }));

  return (
    /*
      🔄 Qui c'era `data-nomi={voci.length}`, che serviva a delle soglie CSS
      tarate sul NUMERO dei nomi. Sono state tolte il 17/09/2026: quanto spazio
      serve non dipende da quanti sono ma da come si chiamano, e a deciderlo è
      ora una misura vera (⇒ la scelta della forma, in testa a questo file).
      ⇒ Niente attributo: un dato che nessuno legge è un dato che il prossimo
        crede in uso.
    */
    <p className="giocatori">
      <Nomi classe="interi" voci={voci.map((v) => v.intero)} />
      <Nomi classe="corti" voci={accorciaElenco(voci)} />
    </p>
  );
}

/**
 * ⭐⭐ **LE DUE FORME STANNO TUTT'E DUE NEL DOM** — 17/09/2026, richiesta sua:
 * *«il cognome puntato [...] quando lo spazio non basta»*.
 *
 * 🔄 **A SCEGLIERE ERA IL CSS, e dalla sera del 17/09 è una misura vera** (⇒ la
 * scelta della forma, in testa a questo file). Qui c'era scritto che il
 * JavaScript non doveva entrarci, *«perché quanto spazio c'è lo sa il CSS e
 * resta l'unico a saperlo»*: il ragionamento reggeva, la premessa no — il CSS
 * sa quanto spazio c'è, ma **non sa quanto ne serve**, perché quello dipende
 * dai cognomi dei soci.
 * ⚖️ Il timore scritto allora — misurare a ogni fotogramma di un trascinamento
 * — è stato evitato, non smentito: si misura **una volta per tutta la griglia**
 * e solo quando la geometria cambia.
 *
 * 💰 Il prezzo dichiarato resta: ogni blocco porta i nomi due volte. Su
 * duecento prenotazioni sono poche decine di parole in più — ed è ciò che
 * permette di cambiare forma senza ridisegnare niente.
 */
function Nomi({ classe, voci }: { classe: 'interi' | 'corti'; voci: string[] }): React.JSX.Element {
  return <span className={classe}>{voci.join(' · ')}</span>;
}

/**
 * `90` → «1 h 30′». Come la scriverebbe una persona, non come la conta un
 * computer.
 *
 * ⚖️ **Non «1 ora e mezza»**, che è la forma dei bottoni dell'area maestro
 * (⇒ `Prenota.tsx`): là le durate sono **tre**, scelte da un elenco, e si
 * leggono una volta; qui il numero cambia a ogni scatto del dito, e una frase
 * che si riscrive venti volte al secondo si legge peggio di due cifre.
 */
export function durataScritta(minuti: number): string {
  const h = Math.floor(minuti / 60);
  const m = minuti % 60;
  if (h === 0) return `${m}′`;
  return m === 0 ? `${h} h` : `${h} h ${m}′`;
}

/**
 * 🏷️ **Come si chiama una prenotazione** (voce 10, 27/09/2026). Col suo tipo,
 * tranne il **corso**, che ha il suo nome: vive mesi e si sceglie da un elenco.
 * ⛔ Solo il corso: i titoli vecchi delle partite (⇒ `regole.ts`, `descrivi`)
 *    non devono tornare a galla.
 */
export function nomeDellaPrenotazione(p: Pick<Prenotazione, 'type' | 'title'>): string {
  const nome = p.type === 'corso' ? p.title?.trim() : '';
  return nome ? nome : etichettaTipo(p.type);
}

export function etichettaTipo(t: Prenotazione['type']): string {
  return {
    partita: 'Partita',
    lezione: 'Lezione',
    allenamento: 'Allenamento',
    manutenzione: 'Manutenzione',
    torneo: 'Torneo',
    corso: 'Corso',
    tattica: 'Partita tattica',
  }[t];
}
